import {
  Alert,
  BottomSheet,
  Button,
  Card,
  Field,
  FieldHint,
  Input,
  Label,
  ProgressBar,
  SegmentedControl,
  Picker,
  StatusPill,
  Textarea,
  cn,
} from "@breenova/ui";
import {
  Activity,
  Ban,
  Bell,
  Bug,
  Check,
  Plus,
  Stethoscope,
  Syringe,
  TriangleAlert,
} from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";

import { DateField } from "@/components/date-field";
import {
  PageHeader,
  RailGroup,
  RailPrimaryAction,
  RailRow,
  RailSection,
} from "@/components/app-shell";
import { useLocations } from "@/lib/birds";
import {
  useCreateHealthRecord,
  useHealthRecords,
  useResolveHealthRecord,
  type HealthFilter,
  type HealthRecordDto,
  type HealthRecordKind,
} from "@/lib/health";

const SEGMENTS = [
  { value: "Active" as const, label: "Running" },
  { value: "Reminders" as const, label: "Reminders" },
  { value: "History" as const, label: "History" },
];

const KINDS: readonly { value: HealthRecordKind; label: string }[] = [
  { value: "Observation", label: "Observation" },
  { value: "Treatment", label: "Treatment" },
  { value: "Medication", label: "Medication" },
  { value: "Vaccination", label: "Vaccination" },
  { value: "Deworming", label: "Deworming" },
  { value: "Quarantine", label: "Quarantine" },
  { value: "VetVisit", label: "Vet visit" },
];

/** Which lucide glyph stands for a kind of record, so the list reads by shape. */
const KIND_ICONS: Record<string, typeof Activity> = {
  Observation: Stethoscope,
  Treatment: Stethoscope,
  Medication: Syringe,
  Vaccination: Syringe,
  Deworming: Bug,
  Quarantine: TriangleAlert,
  VetVisit: Stethoscope,
};

export function HealthPage() {
  const [filter, setFilter] = useState<HealthFilter>("Active");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();

  // The quick-log sheet links here with ?new=1. Consume the flag so a refresh does not
  // reopen the form over whatever the breeder navigated to next.
  useEffect(() => {
    if (params.get("new") === "1") {
      setSheetOpen(true);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const records = useHealthRecords(filter);
  const items = useMemo(() => records.data?.items ?? [], [records.data]);
  const months = useMemo(() => groupByMonth(items), [items]);

  /*
   * The reminders, fetched alongside whatever segment is on screen.
   *
   * The aside shows them on every segment, so they cannot come from `records` — that is
   * the segment's own list, and on History it holds last March. One extra query, cached
   * under its own key, and free whenever the breeder is standing on the Reminders
   * segment because it is then the same query.
   */
  const reminders = useHealthRecords("Reminders");

  const live = items.filter((record) => record.isWithdrawalActive).length;

  return (
    <div className="flex flex-col md:gap-lg">
      <RailPrimaryAction>
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className={cn(
            "flex h-12 w-full items-center justify-center gap-2 rounded-control",
            "bg-action text-cta-sm text-on-action",
            "transition-opacity hover:opacity-90",
          )}
        >
          <Plus className="size-4.5" aria-hidden />
          Add a record
        </button>
      </RailPrimaryAction>

      <RailSection>
        <RailGroup label="Care">
          {SEGMENTS.map((segment) => (
            <RailRow
              key={segment.value}
              label={segment.label}
              selected={filter === segment.value}
              onClick={() => setFilter(segment.value)}
              trailing={
                filter === segment.value && records.data ? records.data.totalCount : undefined
              }
            />
          ))}
        </RailGroup>
      </RailSection>

      <PageHeader
        title="Care"
        subtitle={
          records.data
            ? [
                filter === "Active"
                  ? `${records.data.totalCount} running`
                  : `${records.data.totalCount} ${records.data.totalCount === 1 ? "record" : "records"}`,
                live > 0 ? `${live} ${live === 1 ? "withdrawal" : "withdrawals"}` : null,
                reminders.data && reminders.data.totalCount > 0
                  ? `${reminders.data.totalCount} ${reminders.data.totalCount === 1 ? "reminder" : "reminders"}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")
            : undefined
        }
      />

      {/* Phone only: the rail carries the same three from `md`. */}
      <div className="px-5 pb-4 xl:hidden">
        <SegmentedControl
          label="Care view"
          value={filter}
          options={SEGMENTS}
          onChange={setFilter}
          className="flex w-full"
        />
      </div>

      {error ? (
        <div className="px-5 md:px-gutter-wide">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {/*
        One column on a phone, a main and an aside from `lg`.

        The reminders leave the list and become a panel of their own, because they answer
        a different question: the list says what is running, the panel says what is
        coming. On a phone that distinction costs a whole screen and they stay a segment.
      */}
      <div className="flex flex-col gap-4 px-5 md:px-gutter-wide lg:grid lg:grid-cols-[minmax(0,1fr)_var(--container-aside-care)] lg:items-start lg:gap-lg">
      <section className="flex flex-col gap-4" aria-live="polite">
        {records.isPending ? (
          <p className="py-xl text-center text-body text-on-surface-variant">Loading…</p>
        ) : records.isError ? (
          <Alert tone="error">Could not load health records.</Alert>
        ) : items.length === 0 ? (
          <Card className="flex flex-col items-center gap-sm p-xl text-center">
            <p className="text-card-title text-on-surface">{emptyTitle(filter)}</p>
            <p className="text-body text-on-surface-variant">{emptyBody(filter)}</p>
          </Card>
        ) : filter === "History" ? (
          // Past records are read by when they happened, so the month is the heading and
          // not a date repeated on every row.
          months.map((month) => (
            <section key={month.key} className="flex flex-col gap-sm">
              <h2 className="text-label-caps uppercase text-on-surface-variant">{month.label}</h2>
              <ul className="grid grid-cols-1 gap-4">
                {month.records.map((record) => (
                  <li key={record.id}>
                    <HealthCard record={record} onError={setError} />
                  </li>
                ))}
              </ul>
            </section>
          ))
        ) : (
          /* One column, not two. The cards used to have the page's whole width and two
             fitted; they now share it with the aside, and a treatment card at half of two
             thirds wraps its dosage line onto three. */
          <ul className="grid grid-cols-1 gap-4">
            {items.map((record) => (
              <li key={record.id}>
                <HealthCard record={record} onError={setError} />
              </li>
            ))}
          </ul>
        )}

      </section>

      <aside className="hidden flex-col gap-md lg:flex">
        <RemindersPanel
          records={reminders.data?.items ?? []}
          pending={reminders.isPending}
          today={new Date().toISOString().slice(0, 10)}
        />

        <Card tone="accent" flat className="flex flex-col gap-sm p-lg">
          <span className="text-label-caps uppercase text-on-secondary-container">
            Cage treatments
          </span>
          <p className="text-detail text-on-secondary-container">
            A treatment logged against a cage stays in each bird&rsquo;s own history after it
            moves — so a bird sold in November still carries what it was given in August.
          </p>
        </Card>
      </aside>
      </div>

      {sheetOpen ? (
        <AddRecordSheet onClose={() => setSheetOpen(false)} onError={setError} />
      ) : null}
    </div>
  );
}

/**
 * What is coming, beside what is running.
 *
 * Follow-ups only — a reminder is a record with a date in front of it, and the list is
 * already sorted by that date. Capped at five: the panel is a glance, and a breeder who
 * wants the whole schedule has the Reminders segment and the calendar.
 */
function RemindersPanel({
  records,
  pending,
  today,
}: {
  records: readonly HealthRecordDto[];
  pending: boolean;
  today: string;
}) {
  return (
    <Card tone="recessed" flat className="flex flex-col gap-md p-lg">
      <span className="text-label-caps uppercase text-on-surface-variant">Reminders</span>

      {pending ? (
        <p className="text-detail text-on-surface-variant">Loading…</p>
      ) : records.length === 0 ? (
        <p className="text-detail text-on-surface-variant">
          Nothing is booked in. A follow-up date on a record puts it here.
        </p>
      ) : (
        <ul className="flex flex-col gap-sm">
          {records.slice(0, 5).map((record) => (
            <li key={record.id}>
              <Card
                shape="row"
                flat
                className="flex items-center gap-sm bg-surface-container-lowest p-md"
              >
                <span
                  aria-hidden
                  className={cn(
                    "flex size-9.5 shrink-0 items-center justify-center rounded-full",
                    record.isOverdue
                      ? "bg-error-container text-on-error-container"
                      : "bg-secondary-fixed text-on-secondary-fixed-variant",
                  )}
                >
                  <Stethoscope className="size-4.5" />
                </span>

                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-subtitle text-on-surface">{record.title}</span>
                  <span className="truncate text-micro font-normal text-on-surface-variant" data-numeric>
                    {record.followUpOn ? relativeDay(record.followUpOn, today) : "No date"}
                  </span>
                </span>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** "Today", "Tomorrow", or the date — a reminder three weeks out wants its own date. */
function relativeDay(iso: string, today: string): string {
  const days = Math.round((Date.parse(iso) - Date.parse(today)) / 86_400_000);

  if (days === 0) {
    return "Today";
  }

  if (days === 1) {
    return "Tomorrow";
  }

  if (days === -1) {
    return "Yesterday";
  }

  const [year, month, day] = iso.split("-").map(Number);
  const label =
    year && month && day
      ? new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
          weekday: days > 0 && days < 7 ? "long" : undefined,
          day: "numeric",
          month: "long",
          timeZone: "UTC",
        })
      : iso;

  return days < 0 ? `${label} — overdue` : label;
}

/**
 * One health record.
 *
 * A live withdrawal takes the whole card in terracotta rather than a stripe down its
 * edge: it is the one state where the answer to "can I sell, show or breed this bird" is
 * no, and that has to be legible from across a birdroom. A record that is only a dated
 * reminder is a single row; everything else is a plain card.
 */
function HealthCard({
  record,
  onError,
}: {
  record: HealthRecordDto;
  onError: (message: string) => void;
}) {
  const resolve = useResolveHealthRecord();
  const course = courseProgress(record);
  const Icon = KIND_ICONS[record.kind] ?? Activity;
  const today = new Date().toISOString().slice(0, 10);

  async function markResolved(): Promise<void> {
    try {
      await resolve.mutateAsync({ id: record.id, isResolved: true });
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Could not update the record.");
    }
  }

  const where = [
    record.birdRingNumber ?? (record.locationCode ? `Cage ${record.locationCode}` : "Aviary wide"),
    // A cage treatment covers several birds; saying so distinguishes it from a record that
    // happens to name one.
    record.subjectCount > 1 ? `${record.subjectCount} birds` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  if (record.isWithdrawalActive) {
    return (
      <div className="flex flex-col gap-3.5 rounded-lg bg-secondary-fixed p-lg text-on-secondary-fixed">
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="flex size-touch shrink-0 items-center justify-center rounded-full bg-secondary-strong text-on-action"
          >
            <Icon className="size-5" />
          </span>

          <div className="flex min-w-0 flex-1 flex-col gap-px">
            <span className="truncate text-pair-title">{record.title}</span>
            <span className="truncate text-caption" data-numeric>
              {where}
            </span>
          </div>

          <StatusPill className="bg-secondary-strong text-on-action">Withdrawal</StatusPill>
        </div>

        {course ? (
          <div className="flex flex-col gap-1.5">
            <div className="flex justify-between gap-md text-node" data-numeric>
              <span>
                Day {course.day} of {course.total}
              </span>
              <span>
                {course.total - course.day === 0
                  ? "Last day"
                  : `${course.total - course.day} ${course.total - course.day === 1 ? "day" : "days"} left`}
              </span>
            </div>
            <div className="flex h-2.5 overflow-hidden rounded-full bg-secondary-strong/18">
              <div
                className="rounded-full bg-secondary-strong transition-[width] duration-300"
                style={{ width: `${Math.round(course.progress * 100)}%` }}
              />
            </div>
          </div>
        ) : null}

        {record.withdrawalEndsOn ? (
          <p className="flex items-center gap-2 text-cell font-semibold" data-numeric>
            <Ban className="size-4 shrink-0" aria-hidden />
            {/* Counted from the last dose. Inside it the bird must not be shown, sold or
                bred from, which is why it outlives the course itself. */}
            No showing or selling until {shortDay(record.withdrawalEndsOn)}
          </p>
        ) : null}

        {!record.isResolved ? (
          <button
            type="button"
            disabled={resolve.isPending}
            onClick={() => void markResolved()}
            className={cn(
              "flex h-12.5 items-center justify-center gap-2 rounded-control",
              "bg-secondary-strong text-cta-sm text-on-action transition-opacity hover:opacity-90",
              "disabled:opacity-45",
            )}
          >
            <Check className="size-5" aria-hidden />
            {resolve.isPending ? "Saving…" : "Course finished"}
          </button>
        ) : null}
      </div>
    );
  }

  // A dated reminder with nothing running: one row, which is what it is.
  if (record.followUpOn && !course && !record.isResolved && record.kind !== "Medication") {
    return (
      <Card className="flex items-center gap-3 px-4 py-3.5">
        <span
          aria-hidden
          className="flex size-touch shrink-0 items-center justify-center rounded-full bg-surface-container text-on-surface-body"
        >
          <Bell className="size-5" />
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-px">
          <span className="truncate text-label text-on-surface">{record.title}</span>
          <span
            className={cn("truncate text-caption", record.isOverdue ? "text-secondary" : "text-on-surface-variant")}
            data-numeric
          >
            Reminder · {relativeDay(record.followUpOn, today)}
          </span>
        </div>

        <button
          type="button"
          aria-label={`Mark ${record.title} done`}
          disabled={resolve.isPending}
          onClick={() => void markResolved()}
          className="flex size-touch shrink-0 items-center justify-center rounded-full text-outline transition-colors hover:bg-surface-container"
        >
          <Check className="size-5" aria-hidden />
        </button>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          className="flex size-touch shrink-0 items-center justify-center rounded-full bg-primary-fixed text-on-primary-fixed-variant"
        >
          <Icon className="size-5" />
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-px">
          <span className="truncate text-pair-title text-on-surface">{record.title}</span>
          <span className="truncate text-caption text-on-surface-variant" data-numeric>
            {where}
            {record.startsOn ? ` · since ${shortDay(record.startsOn)}` : ""}
          </span>
        </div>

        {record.isOverdue ? <StatusPill tone="attention">Overdue</StatusPill> : null}

        {!record.isResolved ? (
          <button
            type="button"
            aria-label={`Mark ${record.title} done`}
            disabled={resolve.isPending}
            onClick={() => void markResolved()}
            className="flex size-touch shrink-0 items-center justify-center rounded-full text-outline transition-colors hover:bg-surface-container"
          >
            <Check className="size-5" aria-hidden />
          </button>
        ) : null}
      </div>

      {course ? (
        <div className="flex flex-col gap-1.5">
          <div className="flex justify-between gap-md text-node text-on-surface" data-numeric>
            <span>{record.dosage ?? "Course"}</span>
            <span>
              Day {course.day} of {course.total}
            </span>
          </div>
          <ProgressBar value={course.progress} label="Treatment progress" />
        </div>
      ) : null}

      {record.followUpOn && !record.isResolved ? (
        <div className="flex items-center justify-between gap-md rounded-inset bg-surface-container px-3.5 py-2.75 text-cell font-semibold text-on-surface">
          <span>Follow up</span>
          <span className="font-bold" data-numeric>
            {relativeDay(record.followUpOn, today)}
          </span>
        </div>
      ) : null}

      {record.withdrawalEndsOn ? (
        <p className="flex items-center gap-2 text-caption text-on-surface-variant" data-numeric>
          <Ban className="size-4 shrink-0" aria-hidden />
          Withdrawal ended {shortDay(record.withdrawalEndsOn)}
        </p>
      ) : null}
    </Card>
  );
}

/** "12 Oct". */
function shortDay(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/**
 * How far through a dosing course a record is.
 *
 * Only meaningful once both ends are known: an open-ended course is one that has not
 * been finished, and drawing it as a bar at some arbitrary fraction would claim
 * knowledge the record does not have.
 */
function courseProgress(
  record: HealthRecordDto,
): { day: number; total: number; progress: number } | null {
  if (!record.endsOn) {
    return null;
  }

  const from = Date.parse(`${record.startsOn ?? record.occurredOn}T00:00:00Z`);
  const to = Date.parse(`${record.endsOn}T00:00:00Z`);
  const now = Date.parse(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`);

  const total = Math.round((to - from) / 86_400_000) + 1;

  if (!Number.isFinite(total) || total <= 0) {
    return null;
  }

  const day = Math.min(total, Math.max(1, Math.round((now - from) / 86_400_000) + 1));

  return { day, total, progress: day / total };
}

/** Records bucketed by the month they happened in, newest month first. */
function groupByMonth(
  records: readonly HealthRecordDto[],
): { key: string; label: string; records: HealthRecordDto[] }[] {
  const byMonth = new Map<string, HealthRecordDto[]>();

  for (const record of records) {
    const key = record.occurredOn.slice(0, 7);
    const existing = byMonth.get(key);

    if (existing) {
      existing.push(record);
    } else {
      byMonth.set(key, [record]);
    }
  }

  return [...byMonth.entries()]
    .sort(([left], [right]) => right.localeCompare(left))
    .map(([key, monthRecords]) => ({ key, label: monthLabel(key), records: monthRecords }));
}

function monthLabel(key: string): string {
  const [year, month] = key.split("-").map(Number);

  if (!year || !month) {
    return key;
  }

  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function emptyTitle(filter: HealthFilter): string {
  return filter === "Active"
    ? "Nothing running"
    : filter === "Reminders"
      ? "No reminders"
      : "No records yet";
}

function emptyBody(filter: HealthFilter): string {
  return filter === "Active"
    ? "No open treatments, live withdrawals or unresolved observations."
    : filter === "Reminders"
      ? "Follow-up dates appear here as they are set."
      : "Treatments and observations appear here once recorded.";
}

function AddRecordSheet({
  onClose,
  onError,
}: {
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const create = useCreateHealthRecord();
  const locations = useLocations(true);

  const [target, setTarget] = useState<"bird" | "location">("location");
  const [locationId, setLocationId] = useState("");
  const [kind, setKind] = useState<HealthRecordKind>("Medication");
  const [title, setTitle] = useState("");
  const [medication, setMedication] = useState("");
  const [dosage, setDosage] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [withdrawalDays, setWithdrawalDays] = useState("");
  const [followUpOn, setFollowUpOn] = useState("");
  const [details, setDetails] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    if (!title.trim()) {
      onError("Give the record a title.");
      return;
    }

    if (target === "location" && !locationId) {
      onError("Choose the cage being treated.");
      return;
    }

    try {
      await create.mutateAsync({
        locationId: target === "location" ? locationId : null,
        birdId: null,
        kind,
        title: title.trim(),
        details: details.trim() || null,
        medication: medication.trim() || null,
        dosage: dosage.trim() || null,
        endsOn: endsOn || null,
        withdrawalDays: withdrawalDays ? Number(withdrawalDays) : null,
        followUpOn: followUpOn || null,
      });

      onClose();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Could not save the record.");
      onClose();
    }
  }

  return (
    <BottomSheet open onClose={onClose} title="Add health record">
      <form id="add-health" onSubmit={handleSubmit} className="flex flex-col gap-md" noValidate>
        <Field>
          <Label>Applies to</Label>
          <Picker
            id="target"
            label="Applies to"
            value={target}
            onChange={(value) => setTarget(value as "bird" | "location")}
            options={[
              { value: "location", label: "A whole cage" },
              { value: "bird", label: "One bird" },
            ]}
          />
          <FieldHint>
            A cage treatment is recorded against the birds in it now, so each one keeps it
            in its own history after it moves.
          </FieldHint>
        </Field>

        {target === "location" ? (
          <Field>
            <Label>Cage</Label>
            <Picker
              id="locationId"
              label="Cage"
              placeholder="Choose a cage…"
              emptyLabel="No cages recorded yet"
              disabled={locations.isPending}
              value={locationId}
              onChange={setLocationId}
              options={(locations.data?.locations ?? []).map((location) => ({
                value: location.id,
                label: location.path,
                hint: `${location.birdCount} ${location.birdCount === 1 ? "bird" : "birds"}`,
              }))}
            />
          </Field>
        ) : (
          <Alert tone="info">
            Open a bird and use its Health tab to record something for one bird.
          </Alert>
        )}

        <Field>
          <Label>Kind</Label>
          <Picker
            id="kind"
            label="Kind"
            value={kind}
            onChange={(value) => setKind(value as HealthRecordKind)}
            options={KINDS.map((option) => ({ value: option.value, label: option.label }))}
          />
        </Field>

        <Field>
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            required
            placeholder="Coccidiosis treatment"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </Field>

        <Field>
          <Label htmlFor="medication">Medication</Label>
          <Input
            id="medication"
            value={medication}
            onChange={(event) => setMedication(event.target.value)}
          />
        </Field>

        <Field>
          <Label htmlFor="dosage">Dosage</Label>
          <Input
            id="dosage"
            placeholder="1 ml per litre"
            value={dosage}
            onChange={(event) => setDosage(event.target.value)}
          />
        </Field>

        <Field>
          <Label>Last dose</Label>
          <DateField
            id="endsOn"
            label="Last dose"
            placeholder="Course still running"
            value={endsOn}
            onChange={setEndsOn}
          />
        </Field>

        <Field>
          <Label htmlFor="withdrawalDays">Withdrawal (days)</Label>
          <Input
            id="withdrawalDays"
            type="number"
            inputMode="numeric"
            min={0}
            max={365}
            data-numeric
            value={withdrawalDays}
            onChange={(event) => setWithdrawalDays(event.target.value)}
          />
          <FieldHint>
            From the bottle. Counted from the last dose — inside it the bird must not be
            shown, sold or bred from.
          </FieldHint>
        </Field>

        <Field>
          <Label>Follow up</Label>
          <DateField
            id="followUpOn"
            label="Follow up"
            placeholder="No follow-up set"
            value={followUpOn}
            onChange={setFollowUpOn}
          />
        </Field>

        <Field>
          <Label htmlFor="details">Notes</Label>
          <Textarea
            id="details"
            rows={3}
            value={details}
            onChange={(event) => setDetails(event.target.value)}
          />
        </Field>

        <Button type="submit" block disabled={create.isPending}>
          {create.isPending ? "Saving…" : "Save record"}
        </Button>
      </form>
    </BottomSheet>
  );
}
